import { Card, CardMedia, Divider, useTheme } from '@material-ui/core';
import { ButtonGroupWithSelection } from 'framework/components/ButtonGroupWithSelection';
import { NoRecordsFound } from 'framework/components/NoRecordsFound';
import { PageBreadcrumbDivider } from 'framework/components/breadcrumbs/PageBreadcrumbDivider';
import { PageBreadcrumbTitle } from 'framework/components/breadcrumbs/PageBreadcrumbTitle';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';
import { SearchbarWithParams } from 'framework/components/search/SearchbarWithParams';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { PageableTableWithColumnSelection } from 'framework/table/PageableTableWithColumnSelection';
import { createEmptyHeader } from 'framework/table/createEmptyHeader';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { createType } from 'framework/utils/createType';
import { IPayment, IPaymentsFilterParams, PaymentExportProp, PaymentExportProps, paymentsQuery_export } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useContext } from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { ExportButtonMenuAndDialog } from 'shared/export/ExportButtonMenuAndDialog';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';
import { RecordContext } from 'shared/records/RecordContext';
import { SelectColumnsButtonAndDialog } from 'shared/selectColumns/SelectColumnsButtonAndDialog';
import { createDetailsHeader } from 'shared/table/createDetailsHeader';
import { FinancialsPageBreadcrumbTitle } from '../FinancialsPageBreadcrumbTitle';
import { Filter } from './Filter';
import { PaymentDialog } from './PaymentDialog';
import { PaymentIncomeOrExpenseStatusChip } from './PaymentIncomeOrExpenseStatusChip';
import { SelectableColumn } from './SelectableColumn';
import { QueryContext } from './context/QueryContext';
import { ViewContext } from './context/ViewContext';

const QuickFilters = createType('all', 'linkedToASale', 'linkedToAPurchase');
export type QuickFilter = typeof QuickFilters[number];

export const PaymentsList = () => {
	const { reload, queryResult, params, setParams } = useQueryContext(QueryContext);
	const strings = useLocalization();
	const theme = useTheme();
	const { columns, setColumns, columnsRecord, headers, quickFilter, setQuickFilter, quickFilterRecord } = useContext(ViewContext);
	const { paymentExportRecord } = useContext(RecordContext);
	const { open, confirm } = useDialogsContext(reload);

	if (queryResult === undefined) {
		return <div></div>;
	}

	const onDetail = (id: string) => {
		open(
			<PaymentDialog
				open
				close={confirm}
				id={id}
			/>
		);
	};

	return (
		<RestrictedPageX
			pageClaim='Payments'
			stickyHeader={
				<div
					className='df-row-ac'
					style={{ paddingBottom: 16 }}>
					<FinancialsPageBreadcrumbTitle />
					<PageBreadcrumbDivider />
					<PageBreadcrumbTitle title={strings.payments} />
					<div className='fg1'></div>
					<PageXHeaderActions>
						<PageReloadButtonX reload={reload} />
					</PageXHeaderActions>
				</div>
			}>
			<div
				className='df-col gap-16'
				style={{ padding: 16 }}>
				<div className='df-row-ac jc-sb'>
					<SearchbarWithParams
						params={params}
						setParams={setParams}
						placeholder={strings.searchVerb}
						style={{ width: '400px' }}
						variant='paper'
					/>
				</div>
				{queryResult.unfilteredCount > 0 && (
					<>
						<Card>
							<div
								className='df-row-ac jc-sb'
								style={{ padding: theme.spacing(1) }}>
								<ButtonGroupWithSelection<QuickFilter>
									options={QuickFilters}
									selected={quickFilter}
									setSelected={setQuickFilter}
									renderOption={t => quickFilterRecord[t]}
								/>
								<div>
									<ExportButtonMenuAndDialog<IPaymentsFilterParams, PaymentExportProp>
										params={params}
										filteredCount={queryResult.filteredCount}
										unfilteredCount={queryResult.unfilteredCount}
										allProps={PaymentExportProps}
										downloadF={paymentsQuery_export}
										fileName={strings.revenues}
										record={paymentExportRecord}
										view='payments'
									/>
									<SelectColumnsButtonAndDialog
										selectedColumns={columns}
										setSelectedColumns={setColumns}
										record={columnsRecord}
										view='payments'
									/>
								</div>
							</div>
							<Divider />
							<div className='df-row jc-sb'>
								<Filter
									params={params}
									onFilter={params => setParams(onResetToPage1(params))}
									paddingLeft={theme.spacing(1.5)}
								/>
							</div>
							<Divider />
							<CardMedia>
								<PageableTableWithColumnSelection<IPayment, SelectableColumn>
									queryResult={queryResult}
									headers={headers}
									sortParams={params.sortParams}
									pageParams={params.pageParams}
									setParams={(sp, pp) => setParams({ ...params, sortParams: sp, pageParams: pp })}
									getKey={inst => inst.id}
									selectedColumns={columns}
									prependColumn={createEmptyHeader<IPayment>('in-out', t => (
										<PaymentIncomeOrExpenseStatusChip item={t} />
									))}
									appendColumn={createDetailsHeader<IPayment>(t => onDetail(t.id))}
								/>
							</CardMedia>
						</Card>
					</>
				)}
				{queryResult.unfilteredCount === 0 && <NoRecordsFound>{strings.noWhatRegistered(strings.payments)}</NoRecordsFound>}
			</div>
		</RestrictedPageX>
	);
};
